import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';

export type NotifyVariant = 'success' | 'error' | 'info';

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type Toast = {
  id: number;
  message: string;
  variant: NotifyVariant;
};

type ConfirmState = {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
};

type NotifyApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

type NotifyContextValue = {
  notify: NotifyApi;
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
};

const NotifyContext = createContext<NotifyContextValue | undefined>(undefined);

const TOAST_MS: Record<NotifyVariant, number> = {
  success: 5500,
  error: 6500,
  info: 4500,
};

const VARIANT_CLASS: Record<NotifyVariant, string> = {
  success: 'border-l-nova-green',
  error: 'border-l-nova-red',
  info: 'border-l-nova-sky',
};

export const NotifyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const idRef = useRef(0);
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, variant: NotifyVariant) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, variant }]);
      window.setTimeout(() => dismiss(id), TOAST_MS[variant]);
    },
    [dismiss]
  );

  const notify = useMemo<NotifyApi>(
    () => ({
      success: (message) => push(message, 'success'),
      error: (message) => push(message, 'error'),
      info: (message) => push(message, 'info'),
    }),
    [push]
  );

  const closeConfirm = useCallback((value: boolean) => {
    confirmResolveRef.current?.(value);
    confirmResolveRef.current = null;
    setConfirmState(null);
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions | string) => {
      const opts = typeof options === 'string' ? { message: options } : options;
      if (confirmResolveRef.current) {
        confirmResolveRef.current(false);
      }
      return new Promise<boolean>((resolve) => {
        confirmResolveRef.current = resolve;
        setConfirmState({
          title: opts.title || 'Please confirm',
          message: opts.message,
          confirmLabel: opts.confirmLabel || 'Confirm',
          cancelLabel: opts.cancelLabel || 'Cancel',
          danger: Boolean(opts.danger),
        });
      });
    },
    []
  );

  useEffect(() => {
    if (!confirmState) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeConfirm(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmState, closeConfirm]);

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  return (
    <NotifyContext.Provider value={value}>
      {children}

      <div
        className="fixed top-20 right-4 z-[110] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`nova-card !p-4 shadow-nova-lg pointer-events-auto border-l-4 ${VARIANT_CLASS[toast.variant]} animate-fade-in-up`}
            role="status"
          >
            <div className="flex items-start gap-3">
              <p className="flex-1 text-sm text-nova-text leading-relaxed">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="text-nova-text3 hover:text-nova-text p-1 -m-1 flex-shrink-0"
                aria-label="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirmState && (
        <div
          className="nova-modal-overlay z-[120]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nova-confirm-title"
          onClick={() => closeConfirm(false)}
        >
          <div
            className="nova-card !p-0 shadow-nova-lg max-w-md w-full overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-nova-border">
              <h3 id="nova-confirm-title" className="text-xl font-bold text-nova-text">
                {confirmState.title}
              </h3>
            </div>
            <div className="p-5 sm:p-6">
              <p className="text-sm text-nova-text2 leading-relaxed whitespace-pre-wrap">{confirmState.message}</p>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
                <button type="button" className="btn-ghost" onClick={() => closeConfirm(false)}>
                  {confirmState.cancelLabel}
                </button>
                <button
                  type="button"
                  className={
                    confirmState.danger
                      ? 'inline-flex items-center justify-center bg-nova-red text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-nova-red/90'
                      : 'btn-primary'
                  }
                  onClick={() => closeConfirm(true)}
                >
                  {confirmState.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </NotifyContext.Provider>
  );
};

export const useNotify = () => {
  const context = useContext(NotifyContext);
  if (context === undefined) {
    throw new Error('useNotify must be used within a NotifyProvider');
  }
  return context;
};
